<template>
  <div data-component-group="line-item-form-kb" data-component="line-item-hero">
    <div class="component-actions">
      <StatusSelect
        title="Change Status"
        type="line"
        :items="lineStatuses"
        :is-disabled="isUpdating"
        :loading="!lineStatuses.length || isLoading"
        :active-item="statusName"
        :on-change-item="changeStatus"
      />
    </div>
    <div class="line-item-details">
      <div class="line-item-title">
        <span v-if="!isLoading">{{ truncateString(lineItemName) }}</span>
      </div>

      <div class="line-item-details-table">
        <div class="line-item-details-box">
          <div>
            {{ $t('progress') }}
            <Tooltip :text="$t('ad_hero_progress_tooltip')" position="top" />
          </div>
          <span v-if="!isLoading"> {{ progress }}% </span>
        </div>

        <div class="line-item-details-box">
          <div>
            {{ $t('impressions') }}
            <Tooltip
              :text="$t('the_value_describes_the_number')"
              :link="impressionsLink"
              :delay="1000"
              position="top"
            />
          </div>
          <span v-if="!isLoading">
            <div v-if="isDelivering" class="delivering-impressions">
              <v-img src="/img/fetching_data.svg" class="svg-spinner" />
              <FetchingDataTooltip />
            </div>
            <span v-else>
              {{ impressions }}
            </span>
          </span>
        </div>

        <div class="line-item-details-box">
          <div>
            {{ $t('status') }}
            <Tooltip :text="$t('ad_hero_status_tooltip')" position="top" />
          </div>
          <span v-if="!isLoading">
            <div v-if="isDelivering" class="delivering-status">
              <span class="status-circle" />
              {{ $t('delivering') }}
            </div>
            <span v-else>{{ statusName }}</span>
          </span>
        </div>

        <div class="line-item-details-box">
          <div>
            {{ $t('archived') }}
          </div>
          <span v-if="!isLoading">
            {{ archivedText }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import StatusSelect from '../../dropdowns/Status';
  import Tooltip from '../../tooltips/Tooltip';
  import FetchingDataTooltip from '../../tooltips/FetchingDataTooltip';
  import { isObj, isNum, isBool } from '../../../utils';

  export default {
    components: {
      StatusSelect,
      Tooltip,
      FetchingDataTooltip,
    },
    props: {
      order: {
        type: Object,
        required: true,
      },
      lineItem: {
        type: Object,
        required: true,
      },
      isLoading: {
        type: Boolean,
        required: true,
      },
      isUpdating: {
        type: Boolean,
        required: true,
      },
      onChangeStatus: {
        type: Function,
        required: true,
      },
    },
    data() {
      return {
        lineItemName: '',
        statusName: '',
        archived: '',
        isDelivering: false,
        progress: 0,
        impressions: 0,
        impressionsLink: import.meta.env.VUE_APP_HELP_IMPRESSIONS_URL,
      };
    },
    computed: {
      lineStatuses() {
        return this.$store.getters['promotions/lineStatuses'];
      },
      archivedText() {
        if (!isBool(this.archived)) {
          return '';
        }

        return this.archived ? 'Yes' : 'No';
      },
    },
    watch: {
      lineItem(nextItem) {
        this.clear();
        this.onReceiveLineItem(nextItem);
      },
    },
    created() {
      this.onReceiveLineItem(this.lineItem);
    },
    methods: {
      onReceiveLineItem(nextItem) {
        if (isObj(nextItem) && isNum(nextItem.id)) {
          this.extractLineItemData(nextItem);
        }
      },
      extractLineItemData(lineItem) {
        const isDelivering = this.isDeliveringItem(lineItem);
        const { name, status, progress, units, isArchived } = lineItem;

        this.isDelivering = isDelivering;
        this.lineItemName = name;
        this.progress = progress;
        this.impressions = units;
        this.statusName = status.name;

        if (isBool(isArchived)) {
          this.archived = isArchived;
        }
      },
      isDeliveringItem(item) {
        return (
          item.status &&
          item.status.name === 'Ready' &&
          this.hasStarted(item.start_date)
        );
      },
      hasStarted(startDate) {
        const minutesSinceStart = this.$dayjs().diff(
          this.$dayjs(startDate),
          'minutes',
        );
        return minutesSinceStart >= 5;
      },
      changeStatus(status) {
        const query = {
          orderId: this.order.id,
          lineItemId: this.lineItem.id,
          statusId: status.id,
        };

        this.onChangeStatus(query);
      },
      truncateString(string) {
        if (string.length < 42) {
          return string;
        }

        return string.substring(0, 42);
      },
      clear() {
        this.lineItemName = '';
        this.statusName = '';
        this.progress = '';
        this.archived = '';
        this.impressions = 0;
        this.isDelivering = false;
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='line-item-form-kb'][data-component='line-item-hero'] {
  padding: $spacing-xl;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: transparent;

  @media (max-width: map.get($breakpoints, sm)) {
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    padding: 14px 24px;
  }

  .component-actions {
    width: 325px;
    position: absolute;
    top: $spacing-xl;
    left: $spacing-xl;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .line-item-details {
    margin-top: 50px;

    .line-item-title {
      min-height: 34px;
      width: 600px;
      font-size: 24px;
      font-weight: 500;
      line-height: 32px;
    }

    .line-item-details-table {
      margin-top: 20px;
      width: 550px;
      height: 44px;
      display: flex;
      justify-content: space-between;
      color: var(--c-text-muted);
      font-size: 12px;
      font-weight: 700;

      .line-item-details-box {
        width: 120px;

        span {
          font-size: 14px;
          font-weight: 600;
          color: #424242;

          .v-theme--dark & {
            color: rgb(var(--v-theme-on-surface));
          }
        }

        .green-status {
          color: #43a047;
        }

        .red-status {
          color: red;
        }

        .delivering-status {
          display: flex;
          align-items: center;
          color: #4caf50;

          .status-circle {
            margin-right: 6px;
            width: 6px;
            height: 6px;
            display: inline-block;
            background-color: #4caf50;
            border-radius: 50%;
          }
        }

        .delivering-impressions {
          display: flex;

          .svg-spinner {
            margin-right: 5px;
            max-width: 20px;
          }

          .text {
            color: #2b2d39;
            font-weight: 400;
            text-decoration: underline;
          }
        }
      }
    }
  }
}
</style>
